import React, { useState, useEffect } from 'react';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, TrendingDown, ShoppingCart, Users, Globe, Smartphone, Store, Package, Bell, Download, Zap, Target, DollarSign, Award, Eye, MousePointer, Heart, MessageCircle, Share2, Camera, CheckCircle, AlertCircle, Truck, Factory } from 'lucide-react';
import { getDashboardData } from '../services/dataService';

const TabDashboard = () => {
  const [activeTab, setActiveTab] = useState('revenue');
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [liveData, setLiveData] = useState(true);

  const refreshData = () => {
    setDashboardData(getDashboardData());
    setLastUpdated(new Date());
  };
  const [dashboardData, setDashboardData] = useState(null);

  // Load data on component mount and set up refresh interval
  useEffect(() => {
    const loadData = () => {
      const data = getDashboardData();
      setDashboardData(data);
      setLastUpdated(new Date());
    };

    // Initial load
    loadData();

    // Set up auto-refresh every 30 seconds when live data is enabled
    let interval;
    if (liveData) {
      interval = setInterval(loadData, 30000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [liveData]);



  // Show loading state while data is being fetched
  if (!dashboardData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading dashboard data...</div>
      </div>
    );
  }

  const MetricCard = ({ title, value, change, icon: Icon, prefix = '', suffix = '' }) => (
    <div className="bg-white rounded-lg shadow-sm border p-3">
      <div className="flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-gray-600 truncate">{title}</p>
          <p className="text-lg font-bold text-gray-900">{prefix}{value}{suffix}</p>
          {change !== undefined && (
            <div className={`flex items-center ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {change >= 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
              <span className="text-xs font-medium">{Math.abs(change)}%</span>
            </div>
          )}
        </div>
        <Icon className="w-6 h-6 text-gray-400 flex-shrink-0" />
      </div>
    </div>
  );

  const RevenueTab = () => (
    <div>
      <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="flex items-center space-x-2">
          <Bell className="w-4 h-4 text-blue-600" />
          <span className="font-medium text-blue-900 text-sm">Live:</span>
          <span className="text-blue-800 text-sm">
            {dashboardData.revenue.overview.activeUsers} active users • 
            {dashboardData.revenue.overview.currentOrders} pending orders
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <MetricCard title="Total Revenue" value="4.25M" change={12.5} icon={TrendingUp} prefix="$" />
        <MetricCard title="Total Orders" value="2,834" change={8.3} icon={ShoppingCart} />
        <MetricCard title="Average AOV" value="1,500" change={5.2} icon={Package} prefix="$" />
        <MetricCard title="Active Customers" value="15.4K" change={15.7} icon={Users} />
        <MetricCard title="Conversion Rate" value="3.2" change={2.1} icon={TrendingUp} suffix="%" />
        <MetricCard title="Cart Abandonment" value="68.5" change={-4.3} icon={TrendingDown} suffix="%" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Channel Revenue</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={dashboardData.revenue.channels}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={60} tick={{fontSize: 10}} />
              <YAxis tick={{fontSize: 10}} />
              <Tooltip formatter={(value) => [`$${(value/1000).toFixed(0)}K`, 'Revenue']} />
              <Bar dataKey="revenue" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Channel Growth</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={dashboardData.revenue.channels}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={60} tick={{fontSize: 10}} />
              <YAxis tick={{fontSize: 10}} />
              <Tooltip formatter={(value) => [`${value}%`, 'Growth']} />
              <Bar dataKey="growth" fill="#10b981" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Customer Journey</h3>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={dashboardData.revenue.customerJourney}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="step" tick={{fontSize: 10}} />
              <YAxis tick={{fontSize: 10}} />
              <Tooltip />
              <Area type="monotone" dataKey="website" stackId="1" stroke="#3b82f6" fill="#3b82f6" />
              <Area type="monotone" dataKey="mobile" stackId="1" stroke="#10b981" fill="#10b981" />
              <Area type="monotone" dataKey="store" stackId="1" stroke="#f59e0b" fill="#f59e0b" />
              <Area type="monotone" dataKey="social" stackId="1" stroke="#ef4444" fill="#ef4444" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Revenue Attribution Models</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={dashboardData.revenue.revenueAttribution}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="model" angle={-45} textAnchor="end" height={60} tick={{fontSize: 10}} />
              <YAxis tick={{fontSize: 10}} />
              <Tooltip formatter={(value) => [`$${(value/1000).toFixed(0)}K`, 'Revenue']} />
              <Bar dataKey="revenue" fill="#8b5cf6" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Revenue Distribution</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={dashboardData.revenue.channels}
                dataKey="revenue"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={30}
                outerRadius={70}
              >
                <Cell fill="#3b82f6" />
                <Cell fill="#10b981" />
                <Cell fill="#f59e0b" />
                <Cell fill="#ef4444" />
                <Cell fill="#8b5cf6" />
              </Pie>
              <Tooltip formatter={(value) => [`$${(value/1000).toFixed(0)}K`, 'Revenue']} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Conversion Rate Comparison</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={dashboardData.revenue.channels}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={60} tick={{fontSize: 10}} />
              <YAxis tick={{fontSize: 10}} />
              <Tooltip formatter={(value) => [`${value}%`, 'Conversion Rate']} />
              <Bar dataKey="conversionRate" fill="#06b6d4" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Cross-Channel Behavior</h3>
          <div className="space-y-3">
            {dashboardData.revenue.crossChannel.map((behavior, index) => (
              <div key={index} className="relative">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-900 truncate">{behavior.behavior}</span>
                  <span className="text-xs font-semibold text-gray-900">${(behavior.revenue/1000).toFixed(0)}K</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full"
                    style={{width: `${(behavior.revenue / dashboardData.revenue.crossChannel[0].revenue * 100)}%`}}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Channel Performance Analytics</h3>
          <div className="space-y-3">
            {dashboardData.revenue.channels.map((channel, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-900">{channel.name}</span>
                  <span className="text-xs text-blue-600">{channel.growth > 0 ? '+' : ''}{channel.growth}%</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="text-center">
                    <div className="font-semibold">${(channel.revenue/1000).toFixed(0)}K</div>
                    <div className="text-gray-600">Revenue</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold">{channel.orders}</div>
                    <div className="text-gray-600">Orders</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold">{channel.conversionRate}%</div>
                    <div className="text-gray-600">Conv Rate</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Inventory Analytics</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={[
                  { name: 'In Stock', value: dashboardData.revenue.inventory.totalSku - dashboardData.revenue.inventory.lowStock - dashboardData.revenue.inventory.outOfStock },
                  { name: 'Low Stock', value: dashboardData.revenue.inventory.lowStock },
                  { name: 'Out of Stock', value: dashboardData.revenue.inventory.outOfStock }
                ]}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={80}
              >
                <Cell fill="#10b981" />
                <Cell fill="#f59e0b" />
                <Cell fill="#ef4444" />
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Stock Level Breakdown</h3>
          <div className="space-y-3">
            {dashboardData.revenue.inventory.categories.map((category, index) => (
              <div key={index} className="relative">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-900">{category.category}</span>
                  <span className="text-xs font-semibold text-gray-900">{category.inStock}/{category.total}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-green-600 h-2 rounded-full"
                    style={{width: `${(category.inStock / category.total * 100)}%`}}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-4">
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">API Integration Status</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {dashboardData.operations.apiIntegrations.map((integration, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-900">{integration.name}</span>
                  <div className={`w-2 h-2 rounded-full ${
                    integration.status === 'Connected' ? 'bg-green-500' :
                    integration.status === 'Warning' ? 'bg-yellow-500' :
                    'bg-red-500'
                  }`}></div>
                </div>
                <div className="text-xs text-gray-600 mb-1">{integration.status}</div>
                <div className="text-xs text-gray-500">Last sync: {integration.lastSync}</div>
                <div className="text-xs text-gray-500">Response: {integration.responseTime}ms</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const OperationsTab = () => (
    <div>
      <div className="mb-4 bg-orange-50 border border-orange-200 rounded-lg p-3">
        <div className="flex items-center space-x-2">
          <Package className="w-4 h-4 text-orange-600" />
          <span className="font-medium text-orange-900 text-sm">Operations:</span>
          <span className="text-orange-800 text-sm">
            {dashboardData.operations.overview.ordersShippedYesterday} shipped • 
            {dashboardData.operations.overview.ordersPastDue} past due
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <MetricCard title="Orders Shipped Yesterday" value={dashboardData.operations.overview.ordersShippedYesterday} change={-15.2} icon={Truck} />
        <MetricCard title="Sent to Production" value={dashboardData.operations.overview.sentToProductionYesterday} change={12.5} icon={Factory} />
        <MetricCard title="MTD Orders Shipped" value={dashboardData.operations.overview.mtdOrdersShipped} change={8.3} icon={Package} />
        <MetricCard title="MTD Production" value={dashboardData.operations.overview.mtdOrdersSentToProduction} change={15.7} icon={Factory} />
        <MetricCard title="Orders Past Due" value={dashboardData.operations.overview.ordersPastDue} change={-22.1} icon={AlertCircle} />
        <MetricCard title="Timeline Compliance" value={dashboardData.operations.overview.mtdTimelineCompliance} change={-5.4} icon={CheckCircle} suffix="%" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">On-Time Performance</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={[
                  { name: 'On Time', value: dashboardData.operations.overview.ordersShippedOnTime },
                  { name: 'Late', value: dashboardData.operations.overview.ordersShippedLate }
                ]}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={80}
              >
                <Cell fill="#10b981" />
                <Cell fill="#ef4444" />
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Production Timeline</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={dashboardData.operations.productionTimeline}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="range" angle={-45} textAnchor="end" height={60} tick={{fontSize: 9}} />
              <YAxis tick={{fontSize: 10}} />
              <Tooltip />
              <Bar dataKey="totalShipped" fill="#3b82f6" />
              <Bar dataKey="onTime" fill="#10b981" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Daily Fulfillment</h3>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={dashboardData.operations.dailyFulfillment}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" tick={{fontSize: 10}} />
              <YAxis tick={{fontSize: 10}} />
              <Tooltip />
              <Line type="monotone" dataKey="shipped" stroke="#3b82f6" strokeWidth={2} />
              <Line type="monotone" dataKey="produced" stroke="#10b981" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );

  const AdvertisingTab = () => (
    <div>
      <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3">
        <div className="flex items-center space-x-2">
          <Target className="w-4 h-4 text-green-600" />
          <span className="font-medium text-green-900 text-sm">Advertising:</span>
          <span className="text-green-800 text-sm">
            ROAS {dashboardData.advertising.overview.netROAS} • CTR {dashboardData.advertising.overview.clickThroughRate}%
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <MetricCard title="Spend Yesterday" value="64.59K" change={8.5} icon={DollarSign} prefix="$" />
        <MetricCard title="MTD Total Spend" value="307.2K" change={12.3} icon={TrendingUp} prefix="$" />
        <MetricCard title="Net ROAS" value="23.94" change={5.8} icon={Target} />
        <MetricCard title="Net Ad To Sale" value="4.18" change={-2.1} icon={Award} suffix="%" />
        <MetricCard title="Cost Per Acquisition" value="48.50" change={-8.3} icon={Users} prefix="$" />
        <MetricCard title="Click Through Rate" value="2.85" change={12.7} icon={TrendingUp} suffix="%" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Campaign Performance</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={dashboardData.advertising.campaignPerformance}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="campaign" angle={-45} textAnchor="end" height={70} tick={{fontSize: 9}} />
              <YAxis tick={{fontSize: 10}} />
              <Tooltip formatter={(value) => [`${value}`, 'ROAS']} />
              <Bar dataKey="roas" fill="#10b981" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Regional Performance</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={dashboardData.advertising.regionalPerformance}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="region" tick={{fontSize: 10}} />
              <YAxis tick={{fontSize: 10}} />
              <Tooltip formatter={(value) => [`${value}`, 'ROAS']} />
              <Bar dataKey="roas" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Monthly Trends</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={dashboardData.advertising.monthlyTrend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" tick={{fontSize: 10}} />
              <YAxis tick={{fontSize: 10}} />
              <Tooltip formatter={(value, name) => [
                name === 'spend' ? `$${(value/1000).toFixed(0)}K` : `$${(value/1000000).toFixed(1)}M`,
                name === 'spend' ? 'Spend' : 'Revenue'
              ]} />
              <Bar dataKey="spend" fill="#ef4444" />
              <Bar dataKey="revenue" fill="#10b981" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Channel Spend Distribution</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={dashboardData.advertising.channelSpend}
                dataKey="spend"
                nameKey="channel"
                cx="50%"
                cy="50%"
                innerRadius={30}
                outerRadius={70}
              >
                <Cell fill="#3b82f6" />
                <Cell fill="#10b981" />
                <Cell fill="#f59e0b" />
                <Cell fill="#ef4444" />
              </Pie>
              <Tooltip formatter={(value) => [`$${(value/1000).toFixed(0)}K`, 'Spend']} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border p-4 mb-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Channel Performance Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {dashboardData.advertising.channelSpend.map((channel, index) => (
            <div key={index} className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-900">{channel.channel}</span>
                <span className="text-xs text-green-600">${(channel.spend/1000).toFixed(0)}K</span>
              </div>
              <div className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-600">Clicks</span>
                  <span className="font-semibold">{(channel.clicks/1000).toFixed(0)}K</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-600">Conversions</span>
                  <span className="font-semibold">{channel.conversions}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-600">Impressions</span>
                  <span className="font-semibold">{(channel.impressions/1000000).toFixed(1)}M</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Top Performing Products</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          {dashboardData.advertising.topProducts.map((product, index) => (
            <div key={index} className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-900 truncate">{product.name}</span>
                <span className="text-xs text-purple-600">{product.category}</span>
              </div>
              <div className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-600">Revenue</span>
                  <span className="font-semibold">${(product.revenue/1000).toFixed(0)}K</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-600">Orders</span>
                  <span className="font-semibold">{product.orders}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const TrafficTab = () => (
    <div>
      <div className="mb-4 bg-purple-50 border border-purple-200 rounded-lg p-3">
        <div className="flex items-center space-x-2">
          <Eye className="w-4 h-4 text-purple-600" />
          <span className="font-medium text-purple-900 text-sm">Traffic:</span>
          <span className="text-purple-800 text-sm">
            {dashboardData.traffic.overview.visitorsYesterday.toLocaleString()} visitors yesterday
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <MetricCard title="Visitors Yesterday" value="11.22K" change={15.2} icon={Users} />
        <MetricCard title="MTD Total Visitors" value="49.4K" change={22.5} icon={Eye} />
        <MetricCard title="Engaged Sessions" value="54.2" change={8.3} icon={MousePointer} suffix="%" />
        <MetricCard title="Bounce Rate" value="46.0" change={-5.2} icon={TrendingDown} suffix="%" />
        <MetricCard title="Organic Visitors" value="19.66K" change={18.7} icon={Globe} />
        <MetricCard title="Paid Visitors" value="19.24K" change={22.3} icon={Target} />
        <MetricCard title="Session Duration" value="185" change={8.5} icon={Eye} suffix="s" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Traffic Sources</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={dashboardData.traffic.trafficSources}
                dataKey="visitors"
                nameKey="source"
                cx="50%"
                cy="50%"
                innerRadius={30}
                outerRadius={70}
              >
                <Cell fill="#3b82f6" />
                <Cell fill="#10b981" />
                <Cell fill="#f59e0b" />
                <Cell fill="#ef4444" />
                <Cell fill="#8b5cf6" />
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Device Breakdown</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={dashboardData.traffic.deviceBreakdown}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="device" tick={{fontSize: 10}} />
              <YAxis tick={{fontSize: 10}} />
              <Tooltip formatter={(value) => [`${(value/1000).toFixed(1)}K`, 'Visitors']} />
              <Bar dataKey="visitors" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Geographic Distribution</h3>
          <div className="space-y-3">
            {dashboardData.traffic.geographicDistribution.map((country, index) => (
              <div key={index} className="relative">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-900">{country.country}</span>
                  <span className="text-xs font-semibold text-gray-900">{country.percentage}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-purple-600 h-2 rounded-full"
                    style={{width: `${country.percentage}%`}}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Campaign Metrics</h3>
          <div className="grid grid-cols-2 gap-4 mb-3">
            <div className="text-center">
              <div className="text-lg font-bold text-gray-900">{dashboardData.traffic.campaignMetrics.totalCampaigns}</div>
              <div className="text-xs text-gray-600">Total Campaigns</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-gray-900">{(dashboardData.traffic.campaignMetrics.totalImpressions/1000000).toFixed(1)}M</div>
              <div className="text-xs text-gray-600">Impressions</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-gray-900">{(dashboardData.traffic.campaignMetrics.landingPageViews/1000).toFixed(0)}K</div>
              <div className="text-xs text-gray-600">Landing Page Views</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-gray-900">{(dashboardData.traffic.campaignMetrics.totalClicks/1000).toFixed(0)}K</div>
              <div className="text-xs text-gray-600">Total Clicks</div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-sm font-bold text-green-600">{dashboardData.traffic.campaignMetrics.clickThroughRate}%</div>
              <div className="text-xs text-gray-600">CTR</div>
            </div>
            <div>
              <div className="text-sm font-bold text-blue-600">${dashboardData.traffic.campaignMetrics.costPerClick}</div>
              <div className="text-xs text-gray-600">CPC</div>
            </div>
            <div>
              <div className="text-sm font-bold text-purple-600">{dashboardData.traffic.campaignMetrics.impressionShare}%</div>
              <div className="text-xs text-gray-600">Impression Share</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Top Landing Pages</h3>
          <div className="space-y-3">
            {dashboardData.traffic.topLandingPages.map((page, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-900 truncate">{page.page}</span>
                  <span className="text-xs text-purple-600">{(page.views/1000).toFixed(1)}K views</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="text-center">
                    <div className="font-semibold">{page.conversions}</div>
                    <div className="text-gray-600">Conversions</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold">{page.conversionRate}%</div>
                    <div className="text-gray-600">Conv Rate</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold">{page.bounceRate}%</div>
                    <div className="text-gray-600">Bounce Rate</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const SocialTab = () => (
    <div>
      <div className="mb-4 bg-pink-50 border border-pink-200 rounded-lg p-3">
        <div className="flex items-center space-x-2">
          <Heart className="w-4 h-4 text-pink-600" />
          <span className="font-medium text-pink-900 text-sm">Social:</span>
          <span className="text-pink-800 text-sm">
            {dashboardData.social.overview.mtdEngagement.toLocaleString()} total engagement • 
            {dashboardData.social.overview.engagementRate}% rate
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <MetricCard title="Posts Yesterday" value={dashboardData.social.overview.postsCreatedYesterday} change={25.0} icon={Camera} />
        <MetricCard title="MTD Total Posts" value={dashboardData.social.overview.mtdTotalPosts} change={15.8} icon={MessageCircle} />
        <MetricCard title="MTD Likes" value="15.48K" change={22.3} icon={Heart} />
        <MetricCard title="MTD Engagement" value="79.39K" change={18.7} icon={Share2} />
        <MetricCard title="Engagement Rate" value={dashboardData.social.overview.engagementRate} change={8.5} icon={TrendingUp} suffix="%" />
        <MetricCard title="Total Reach" value="1.35M" change={12.4} icon={Eye} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Platform Performance</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={dashboardData.social.platformBreakdown}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="platform" tick={{fontSize: 10}} />
              <YAxis tick={{fontSize: 10}} />
              <Tooltip formatter={(value) => [`${(value/1000).toFixed(0)}K`, 'Followers']} />
              <Bar dataKey="followers" fill="#ec4899" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Content Types</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={dashboardData.social.contentTypes}
                dataKey="engagement"
                nameKey="type"
                cx="50%"
                cy="50%"
                innerRadius={30}
                outerRadius={70}
              >
                <Cell fill="#ec4899" />
                <Cell fill="#8b5cf6" />
                <Cell fill="#06b6d4" />
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Story Analytics</h3>
          <div className="grid grid-cols-2 gap-4 mb-3">
            <div className="text-center">
              <div className="text-lg font-bold text-gray-900">{(dashboardData.social.storyMetrics.storyViews/1000).toFixed(0)}K</div>
              <div className="text-xs text-gray-600">Story Views</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-gray-900">{(dashboardData.social.storyMetrics.storyReach/1000).toFixed(0)}K</div>
              <div className="text-xs text-gray-600">Story Reach</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-gray-900">{(dashboardData.social.storyMetrics.forwardTaps/1000).toFixed(1)}K</div>
              <div className="text-xs text-gray-600">Forward Taps</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-gray-900">{dashboardData.social.storyMetrics.completionRate}%</div>
              <div className="text-xs text-gray-600">Completion Rate</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 text-center">
            <div>
              <div className="text-sm font-bold text-red-600">{(dashboardData.social.storyMetrics.exits/1000).toFixed(1)}K</div>
              <div className="text-xs text-gray-600">Exits</div>
            </div>
            <div>
              <div className="text-sm font-bold text-blue-600">{(dashboardData.social.storyMetrics.websiteClicks/1000).toFixed(1)}K</div>
              <div className="text-xs text-gray-600">Website Clicks</div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Hashtag Performance</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={dashboardData.social.hashtagPerformance}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hashtag" angle={-45} textAnchor="end" height={60} tick={{fontSize: 9}} />
              <YAxis tick={{fontSize: 10}} />
              <Tooltip formatter={(value) => [`${(value/1000).toFixed(0)}K`, 'Reach']} />
              <Bar dataKey="reach" fill="#ec4899" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Audience Demographics</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={dashboardData.social.audienceInsights}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="demographic" tick={{fontSize: 10}} />
              <YAxis tick={{fontSize: 10}} />
              <Tooltip formatter={(value, name) => [
                name === 'percentage' ? `${value}%` : `${value}%`,
                name === 'percentage' ? 'Audience' : 'Engagement'
              ]} />
              <Bar dataKey="percentage" fill="#8b5cf6" />
              <Bar dataKey="engagement" fill="#06b6d4" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Hashtag Details</h3>
          <div className="space-y-3">
            {dashboardData.social.hashtagPerformance.map((hashtag, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-900">{hashtag.hashtag}</span>
                  <span className="text-xs text-pink-600">{hashtag.posts} posts</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="text-center">
                    <div className="font-semibold">{(hashtag.reach/1000).toFixed(0)}K</div>
                    <div className="text-gray-600">Reach</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold">{(hashtag.engagement/1000).toFixed(1)}K</div>
                    <div className="text-gray-600">Engagement</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Top Posts</h3>
          <div className="space-y-3">
            {dashboardData.social.topPosts.map((post, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-900 truncate">{post.content}</span>
                  <span className="text-xs text-pink-600">{(post.engagement/1000).toFixed(1)}K eng</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="text-center">
                    <div className="font-semibold">{(post.likes/1000).toFixed(1)}K</div>
                    <div className="text-gray-600">Likes</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold">{post.comments}</div>
                    <div className="text-gray-600">Comments</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold">{post.shares}</div>
                    <div className="text-gray-600">Shares</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Omnichannel Dashboard</h1>
              <p className="text-gray-600 mt-1">Real-time analytics and insights for apparel business</p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={refreshData}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                <span>Refresh Data</span>
              </button>
              <div className="flex items-center space-x-2">
                <div className={`w-2 h-2 rounded-full ${liveData ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                <span className="text-sm text-gray-600">
                  {liveData ? 'Auto-refresh: 30s' : 'Static'} • Last updated: {lastUpdated.toLocaleTimeString()}
                </span>
              </div>
              <button
                onClick={() => setLiveData(!liveData)}
                className="px-3 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {liveData ? 'Disable' : 'Enable'} Live Data
              </button>
            </div>
          </div>

          <div className="flex space-x-1 bg-white rounded-lg p-1 shadow-sm border">
            {[
              { id: 'revenue', label: 'Revenue', icon: DollarSign },
              { id: 'operations', label: 'Operations', icon: Package },
              { id: 'advertising', label: 'Advertising', icon: Target },
              { id: 'traffic', label: 'Traffic', icon: Eye },
              { id: 'social', label: 'Social', icon: Heart }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="transition-all duration-300">
          {activeTab === 'revenue' && <RevenueTab />}
          {activeTab === 'operations' && <OperationsTab />}
          {activeTab === 'advertising' && <AdvertisingTab />}
          {activeTab === 'traffic' && <TrafficTab />}
          {activeTab === 'social' && <SocialTab />}
        </div>
      </div>
    </div>
  );
};

export default TabDashboard;